// Fatia S5b: detalhe de uma conta no backoffice (assinatura, limites e overrides, empresas, equipe, conexões, ações e auditoria).
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import { OVERRIDABLE_KEYS, type AdminAccountDetail, type OverridableKey } from '@impulso/shared/api/admin'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PageHead, Platform } from '../../ui'
import { useToast } from '../../ui/toast'
import { AdminNav, AuditTable } from '../../features/admin/AdminNav'
import { dateTime, useAdminAccount, useResync, useSetOverride } from '../../features/admin/api'
import { accountStatusLabel, enumLabel } from '../../features/admin/labels'

const KEY_LABEL: Record<OverridableKey, string> = {
  maxCompanies: 'Máximo de empresas', maxUsers: 'Máximo de usuários', managedSpendPerCompany: 'Investimento gerenciado por empresa (R$)',
  autopilotModes: 'Modos do piloto', aiCreditsPerCompany: 'Créditos de IA por empresa', weeklyIdeas: 'Ideias da semana',
  whiteLabel: 'Marca própria', clientPortal: 'Portal do cliente', syncIntervalMinutes: 'Intervalo de sincronização (min)',
}
const BOOL_KEYS: OverridableKey[] = ['weeklyIdeas', 'whiteLabel', 'clientPortal']
const CONN_CLS: Record<string, string> = { active: 'green', needs_reconnect: 'amber', error: 'red', disconnected: '' }
const show = (v: unknown) => (typeof v === 'boolean' ? (v ? 'Sim' : 'Não') : Array.isArray(v) ? v.map((x) => enumLabel(String(x))).join(', ') : String(v))

export default function AdminAccount() {
  const { accountId = '' } = useParams()
  const q = useAdminAccount(accountId)
  return (
    <>
      <PageHead title={q.data?.account.name ?? 'Conta'} sub={q.data ? `${q.data.account.kind === 'agency' ? 'Agência' : 'Negócio'} · ${accountStatusLabel(q.data.account.status)} · desde ${new Date(q.data.account.createdAt).toLocaleDateString('pt-BR')}` : undefined}>
        <Link className="btn sm" to="/admin/contas"><ArrowLeft size={15} /> Voltar</Link>
      </PageHead>
      <AdminNav />
      {q.isLoading && <div className="card"><Loading lines={6} /></div>}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && <Detail d={q.data} accountId={accountId} />}
    </>
  )
}

function Detail({ d, accountId }: { d: AdminAccountDetail; accountId: string }) {
  const resync = useResync()
  const toast = useToast()
  const overrideKeys = new Set(d.overrides.map((o) => o.key))
  return (
    <div className="stack">
      <div className="grid g-main">
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Limites efetivos</h3>
          <div className="small muted" style={{ marginBottom: 10 }}>
            Plano: <strong>{d.subscription?.planId ?? 'sem assinatura'}</strong>
            {d.subscription && <> · {enumLabel(d.subscription.status)} · {enumLabel(d.subscription.provider)}{d.subscription.trialEndsAt ? ` · teste até ${new Date(d.subscription.trialEndsAt).toLocaleDateString('pt-BR')}` : ''}</>}
          </div>
          <div className="list">
            {OVERRIDABLE_KEYS.map((k) => (
              <div key={k} className="list-item between" style={{ alignItems: 'center' }}>
                <span className="small">{KEY_LABEL[k]}</span>
                <span className="row" style={{ gap: 6 }}>
                  {overrideKeys.has(k) && <span className="badge purple">override</span>}
                  <strong className="small">{show(d.entitlements[k])}</strong>
                </span>
              </div>
            ))}
          </div>
        </div>
        <OverrideForm accountId={accountId} d={d} />
      </div>

      <div className="grid g-2">
        <div className="card flush">
          <h3 style={{ padding: '16px 16px 8px' }}>Empresas ({d.companies.length})</h3>
          <div className="table-wrap"><table className="table"><tbody>
            {d.companies.map((c) => <tr key={c.id}><td>{c.name}</td><td><span className={`badge ${c.status === 'active' ? 'green' : ''}`}>{enumLabel(c.status)}</span></td></tr>)}
          </tbody></table></div>
        </div>
        <div className="card flush">
          <h3 style={{ padding: '16px 16px 8px' }}>Equipe ({d.members.length})</h3>
          <div className="table-wrap"><table className="table"><tbody>
            {d.members.map((m) => <tr key={m.userId}><td><div className="strong small">{m.name}</div><div className="small muted">{m.email}</div></td><td className="small">{enumLabel(m.role)}</td><td className="small muted hide-xs">{dateTime(m.lastLoginAt)}</td></tr>)}
          </tbody></table></div>
        </div>
      </div>

      <div className="card flush">
        <h3 style={{ padding: '16px 16px 8px' }}>Conexões e saúde</h3>
        {d.connections.length === 0 ? <p className="small muted" style={{ padding: 16 }}>Nenhuma conta de anúncios conectada.</p> : (
          <div className="table-wrap"><table className="table stack-sm">
            <thead><tr><th>Plataforma</th><th>Empresa</th><th>Situação</th><th>Última sincronização</th><th>Erro</th><th /></tr></thead>
            <tbody>
              {d.connections.map((c) => (
                <tr key={c.id}>
                  <td><Platform platform={c.provider} label={c.externalAccountName ?? c.externalAccountId} /></td>
                  <td className="small" data-label="Empresa">{c.companyName}</td>
                  <td data-label="Situação"><span className={`badge ${CONN_CLS[c.status] ?? ''}`}>{enumLabel(c.status)}</span></td>
                  <td className="small" data-label="Última sincronização">{dateTime(c.lastSyncAt)}</td>
                  <td className="small muted" data-label="Erro" style={{ maxWidth: 240 }}>{c.lastError ?? '—'}</td>
                  <td data-label="">
                    <button className="btn sm" disabled={resync.isPending} onClick={() => resync.mutate(c.id, { onSuccess: (r) => toast(r.message), onError: (e) => toast(errorMessage(e)) })}>
                      <RefreshCw size={14} /> Reprocessar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card flush">
        <h3 style={{ padding: '16px 16px 8px' }}>Últimas ações nas plataformas</h3>
        {d.recentActions.length === 0 ? <p className="small muted" style={{ padding: 16 }}>Nenhuma ação ainda.</p> : (
          <div className="table-wrap"><table className="table stack-sm">
            <thead><tr><th>Quando</th><th>Origem</th><th>Situação</th><th>Resumo</th></tr></thead>
            <tbody>{d.recentActions.map((a) => (
              <tr key={a.id}><td className="small nowrap" data-label="Quando">{dateTime(a.createdAt)}</td><td className="small" data-label="Origem">{enumLabel(a.origin)}</td><td data-label="Situação"><span className={`badge ${a.status === 'succeeded' ? 'green' : a.status === 'failed' ? 'red' : ''}`}>{enumLabel(a.status)}</span></td><td className="small" data-label="Resumo">{a.summary}{a.error && <div className="muted">{a.error}</div>}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      <div className="card flush">
        <h3 style={{ padding: '16px 16px 8px' }}>Auditoria da conta</h3>
        <AuditTable items={d.recentAudit} />
      </div>
    </div>
  )
}

function OverrideForm({ accountId, d }: { accountId: string; d: AdminAccountDetail }) {
  const set = useSetOverride(accountId)
  const toast = useToast()
  const [key, setKey] = useState<OverridableKey>('maxCompanies')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const isBool = BOOL_KEYS.includes(key)

  const parse = (): unknown => {
    if (isBool) return value === 'true'
    if (key === 'autopilotModes') return value.split(',').map((s) => s.trim()).filter(Boolean)
    const n = Number(value)
    return Number.isFinite(n) ? n : value
  }
  const submit = (remove: boolean) => {
    setErr(null)
    set.mutate({ key, value: remove ? null : parse(), reason }, {
      onSuccess: () => { toast(remove ? 'Override removido.' : 'Override salvo.'); setValue(''); setReason('') },
      onError: (e) => setErr(errorMessage(e)),
    })
  }
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <h3>Ajustar limite (override)</h3>
      <div className="field">
        <label htmlFor="ok">Limite</label>
        <select id="ok" className="select" value={key} onChange={(e) => { setKey(e.target.value as OverridableKey); setValue('') }}>
          {OVERRIDABLE_KEYS.map((k) => <option key={k} value={k}>{KEY_LABEL[k]}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor="ov">Novo valor</label>
        {isBool ? (
          <select id="ov" className="select" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Escolha…</option><option value="true">Sim</option><option value="false">Não</option>
          </select>
        ) : (
          <input id="ov" className="input" value={value} onChange={(e) => setValue(e.target.value)} placeholder={key === 'autopilotModes' ? 'suggest, copilot, auto' : 'Ex.: 10'} />
        )}
        <span className="small muted">Atual: {show(d.entitlements[key])}</span>
      </div>
      <div className="field">
        <label htmlFor="or">Motivo (fica na auditoria)</label>
        <input id="or" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: parceria até dezembro" />
      </div>
      {err && <div className="form-error" role="alert">{err}</div>}
      <div className="form-actions">
        {(!value || reason.trim().length < 3) && <span className="small muted" style={{ marginRight: 'auto' }}>{!value ? 'Informe o novo valor' : 'Escreva o motivo (mínimo 3 letras)'} para salvar.</span>}
        {d.overrides.some((o) => o.key === key) && <button className="btn" disabled={reason.trim().length < 3 || set.isPending} onClick={() => submit(true)}>Remover override</button>}
        <button className="btn primary" disabled={!value || reason.trim().length < 3 || set.isPending} onClick={() => submit(false)}>Salvar override</button>
      </div>
    </div>
  )
}
